import { motion } from "framer-motion";
import { Bell, AlertTriangle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDistrict } from "../utils/DistrictContext";
import { getNotificationStatusList } from "../services";
import { useTheme } from "../context/ThemeContext";
import { Spin } from "antd";

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// const impactClass = (impact) => {
//   const val = toNumber(impact);

//   // You can tweak these colors later
//   if (val <= 0.2) return "bg-green-500/10 border-green-500/30 text-green-400";
//   if (val <= 0.4) return "bg-yellow-500/10 border-yellow-500/30 text-yellow-400";
//   return "bg-red-500/10 border-red-500/30 text-red-400";
// };

const impactClass = (impact) => {
  const val = toNumber(impact);

  if (val <= 0.2) {
    return "bg-green-500/10 border-green-500/30 text-green-400";
  }
  if (val <= 0.4) {
    return "bg-yellow-500/10 border-yellow-500/30 text-yellow-400";
  }
  return "bg-red-500/10 border-red-500/30 text-red-400";
};

const Notification = () => {
  const selectedDistrict = useDistrict();
  const { colors } = useTheme();
  const district = selectedDistrict?.selectedDistrict;

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("MCData") || "{}");
    } catch {
      return {};
    }
  }, []);
  const userId = user?.userid;

  const [loading, setLoading] = useState(false);
  const [notGood, setNotGood] = useState([]);
  const [good, setGood] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userId || !district) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        // service returns: { data: { ... }, status: true }
        const res = await getNotificationStatusList({
          user_id: userId,
          district,
        });

        const payload = res?.data || res;
        const ng = payload?.meter_status_list_not_good || [];
        const g = payload?.meter_status_list_good || [];

        const sorted = [...ng].sort(
          (a, b) => toNumber(b?.impact) - toNumber(a?.impact),
        );

        setNotGood(sorted);
        setGood(g);
      } catch (e) {
        console.error("notification_status_list failed", e);
        setError("Failed to load notifications");
        setNotGood([]);
        setGood([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, district]);

  const noMonitored = !loading && notGood.length === 0 && good.length === 0;

  return (
    <motion.div
      className="space-y-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Loading */}
      {loading && (
        <div
          className=" backdrop-blur-sm rounded-xl border  p-6 text-center"
          style={{
            background: colors.background,
            borderColor: colors.border,
            color: colors.text,
          }}
        >
          <div className="flex items-center justify-center h-full">
                                        <Spin size="large" />
                                      </div>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-300">
          {error}
        </div>
      )}

      {/* No meters monitored */}
      {!loading && !error && noMonitored && (
        <div className="space-y-4">
          {/* Warning Banner */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0" />
            <p className="text-yellow-500 font-medium">
              No Meters Are Currently Being Monitored.
            </p>
          </div>

          {/* Empty State */}
          <div
            className=" backdrop-blur-sm rounded-xl border p-12 text-center"
            style={{
              background: colors.background,
              borderColor: colors.border,
            }}
          >
            <Bell
              className="w-16 h-16 mx-auto mb-4 opacity-50"
              style={{ color: colors.primary }}
            />
            <h3
              className="text-xl font-semibold mb-2"
              style={{ color: colors.text }}
            >
              No Active Notifications
            </h3>
            <p style={{ color: colors.textSecondary }}>
              When meters are monitored, notifications will appear here
            </p>
          </div>
        </div>
      )}

      {/* ISSUE METERS (not good) */}
      {!loading && !error && notGood.length > 0 && (
        <div className="space-y-4">
          {notGood.map((m, idx) => {
            const meterLabel = m?.register_number
              ? `${m?.meter_id}-${m?.register_number}`
              : `${m?.meter_id}`;

            const impactVal = toNumber(m?.impact);

            return (
              <div
                key={`${m?.meter_id}-${m?.register_number || ""}-${idx}`}
                className=" backdrop-blur-sm rounded-xl border p-5"
                style={{
                  background: colors.background,
                  borderColor: colors.border,
                }}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <p className="font-semibold" style={{ color: colors.text }}>
                      {m?.overided_name || m?.account_name || "Meter"}
                    </p>
                    <p
                      className="text-sm"
                      style={{ color: colors.textSecondary }}
                    >
                      {meterLabel}
                    </p>
                  </div>

                  {/* <div
                    className={`px-3 py-1 rounded-full text-xs border ${impactClass(
                      impactVal
                    )}`}
                  >
                    Impact: {impactVal.toFixed(2)}
                  </div> */}
                </div>

                <div className="space-y-2">
                  {(m?.status || []).map((s, sIdx) => (
                    <div
                      key={sIdx}
                      className={`rounded-lg px-4 py-2 text-sm border ${impactClass(
                        m?.impact,
                      )}`}
                    >
                      {s}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* GOOD METERS */}
      {!loading && !error && good.length > 0 && (
        <div
          className=" backdrop-blur-sm rounded-xl border p-5"
          style={{
            background: colors.background,
            borderColor: colors.border,
          }}
        >
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4 text-green-300">
            Meters below have no issues reported.
          </div>

          <div className="divide-y" style={{ borderColor: colors.border }}>
            {good.map((m, idx) => {
              const meterLabel = m?.register_number
                ? `${m?.meter_id}-${m?.register_number}`
                : `${m?.meter_id}`;

              return (
                <div
                  key={`${m?.meter_id}-${m?.register_number || ""}-${idx}`}
                  className="py-3 flex justify-between gap-4"
                >
                  <p className="text-sm" style={{ color: colors.text }}>
                    {m?.account_name || m?.overided_name || "Account"}
                  </p>
                  <p
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    {meterLabel}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Notification;
